import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Calendar,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  cn,
  Flex,
  Text,
} from '@kognitos/lattice';
import {
  createDate,
  DATE_FORMAT_TOKENS,
  formatDate,
  formatTimeDisplay,
  getCurrentDate,
  parseTime,
} from '@/shared/utils/datetime-utils';
import { usePreviewAutomationSchedule } from '@/generated/hooks/grimoire/v1/api';
import type { IScheduleConfig } from './ScheduleTriggersDialog';
import {
  addMoreScheduledDates,
  buildScheduleConfig,
  formatScheduleTimeDisplay,
  getScheduledDates,
  getValidTimeOrDefault,
  TIMEZONE_SHORT_ABBR,
} from './utils';

/** Number of items to show initially before "View more" */
const INITIAL_DISPLAY_COUNT = 5;

/** Debounce delay in ms for preview API calls */
const PREVIEW_DEBOUNCE_MS = 500;

/**
 * Represents a scheduled run occurrence in the list view.
 */
export interface IScheduledRun {
  date: Date;
  startTime: string;
  endTime?: string;
  dayOfWeek: string;
  timezone: string;
}

/**
 * Props for the SchedulePreview component.
 */
export interface ISchedulePreviewProps {
  /** Schedule configuration */
  scheduleConfig?: IScheduleConfig;
  /** Already-known summary; skips the preview API when provided */
  summary?: string;
  /** Places the list beside the calendar instead of below it */
  orientation?: 'vertical' | 'horizontal';
  /** Hides the "View more" control, for read-only previews */
  showViewMore?: boolean;
  /** Organization ID for preview API */
  organizationId?: string;
  /** Workspace ID for preview API */
  workspaceId?: string;
  /** Automation ID for preview API */
  automationId?: string;
}

/**
 * Shows a calendar view with highlighted scheduled days and a list view
 * of upcoming runs with their dates and times.
 *
 * Fetches the schedule summary from the backend preview API when
 * organization/workspace/automation IDs are available.
 *
 * @param props - Component props
 * @returns JSX element containing the schedule preview card
 */
export function SchedulePreview({
  scheduleConfig,
  summary,
  orientation = 'vertical',
  showViewMore = true,
  organizationId,
  workspaceId,
  automationId,
}: ISchedulePreviewProps) {
  const { t } = useTranslation('automation');
  const isHorizontal = orientation === 'horizontal';

  // Calendar display month state
  const [displayMonth, setDisplayMonth] = useState(() => getCurrentDate());

  // Displayed count for list view
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  // Track previous config to detect changes and reset state
  const prevConfigRef = useRef<IScheduleConfig | undefined>(undefined);

  // Backend preview state for summary description
  const [previewSummary, setPreviewSummary] = useState<string | undefined>(
    undefined,
  );
  const previewMutation = usePreviewAutomationSchedule();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scheduled dates state - starts with initial batch, can be extended via pagination
  const [scheduledDates, setScheduledDates] = useState<Date[]>(() =>
    scheduleConfig ? getScheduledDates(scheduleConfig) : [],
  );

  // Call preview API with debounce when config changes to get summary
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to fetch preview summary when config changes
  useEffect(() => {
    if (
      !scheduleConfig?.frequency ||
      !organizationId ||
      !workspaceId ||
      !automationId
    ) {
      setPreviewSummary(undefined);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      previewMutation.mutate(
        {
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            config: buildScheduleConfig(scheduleConfig),
          },
        },
        {
          onSuccess: response => {
            setPreviewSummary(response.data.summary);
          },
          onError: () => {
            setPreviewSummary(undefined);
          },
        },
      );
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleConfig, organizationId, workspaceId, automationId]);

  // Reset scheduled dates when config changes
  if (scheduleConfig !== prevConfigRef.current) {
    prevConfigRef.current = scheduleConfig;
    const newDates = scheduleConfig ? getScheduledDates(scheduleConfig) : [];
    if (
      newDates.length !== scheduledDates.length ||
      newDates.some((d, i) => d.getTime() !== scheduledDates[i]?.getTime())
    ) {
      setScheduledDates(newDates);
      setDisplayCount(INITIAL_DISPLAY_COUNT);
      setDisplayMonth(getCurrentDate());
    }
  }

  // Load more scheduled dates (pagination)
  const loadMoreDates = useCallback(() => {
    if (!scheduleConfig) return;
    const newDates = addMoreScheduledDates(scheduleConfig, scheduledDates);
    setScheduledDates(prev => [...prev, ...newDates]);
  }, [scheduleConfig, scheduledDates]);

  // Compute scheduled runs for list view from scheduled dates
  const scheduledRuns = useMemo((): IScheduledRun[] => {
    if (!scheduleConfig) return [];

    const { repeatStartTime, timezone } = scheduleConfig;
    const timezoneAbbr = timezone
      ? (TIMEZONE_SHORT_ABBR[timezone] ?? timezone)
      : 'PT';

    return scheduledDates.map(date => ({
      date,
      startTime: getValidTimeOrDefault(repeatStartTime),
      // TODO: Re-enable when backend supports active_window
      // endTime: repeatEndTime,
      endTime: undefined,
      dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' }),
      timezone: timezoneAbbr,
    }));
  }, [scheduledDates, scheduleConfig]);

  // Visible runs based on display count
  const visibleRuns = scheduledRuns.slice(0, displayCount);

  // Handles "View more" button click - expands list by 5 more items and loads more dates if needed
  const handleViewMore = () => {
    const newDisplayCount = displayCount + INITIAL_DISPLAY_COUNT;
    if (newDisplayCount >= scheduledDates.length) {
      loadMoreDates();
    }
    setDisplayCount(newDisplayCount);
  };

  // Handles calendar month navigation - loads more dates when navigating forward past loaded dates
  const handleMonthChange = (newMonth: Date) => {
    setDisplayMonth(newMonth);

    const lastDate = scheduledDates[scheduledDates.length - 1];
    if (lastDate) {
      // Check the first week of the next month for any future dates that
      // are shown in the current month's view
      const startOfNextMonth = createDate(
        newMonth.getFullYear(),
        newMonth.getMonth() + 1,
        7,
      );
      if (startOfNextMonth > lastDate) {
        loadMoreDates();
      }
    }
  };

  const isEmpty = !scheduleConfig || scheduledDates.length === 0;

  // Blank state - centered text only, no calendar or list
  if (isEmpty) {
    return (
      <Card
        className="flex h-full w-[280px] justify-center bg-transparent"
        data-testid="schedule-preview-empty"
      >
        <Flex direction="column" align="center" justify="center" paddingX={4}>
          <Text level="base" weight="medium">
            {t('automation.schedule.preview.title')}
          </Text>
          <Text level="small" weight="normal" color="muted">
            {t('automation.schedule.preview.blank')}
          </Text>
        </Flex>
      </Card>
    );
  }

  // Full state with calendar and list
  return (
    <Card
      className={cn(
        'flex bg-transparent pt-4 gap-4',
        isHorizontal
          ? // Sized to content: the list is capped at five runs, so there is
            // nothing to grow into a fixed height.
            'h-auto max-h-[460px] w-auto overflow-hidden border-0 shadow-none'
          : 'h-full w-[280px] overflow-y-auto',
      )}
      data-testid="schedule-preview"
    >
      <CardHeader>
        {/* The hover card is titled by the badge it opens from. */}
        {!isHorizontal && (
          <Text level="base" weight="medium">
            {t('automation.schedule.preview.title')}
          </Text>
        )}
        <CardDescription>
          {summary ?? previewSummary ?? t('automation.schedule.preview.blank')}
        </CardDescription>
      </CardHeader>

      <Flex
        direction={isHorizontal ? 'row' : 'column'}
        gap={4}
        align="start"
        width="full"
        className={cn(isHorizontal && 'min-h-0 flex-1')}
      >
        <CardContent className="px-4">
          {/* Calendar View - Display only for days, navigation still clickable */}
          <Calendar
            key={scheduledDates.map(d => d.toISOString()).join(',')}
            mode="multiple"
            selected={scheduledDates}
            month={displayMonth}
            onMonthChange={handleMonthChange}
            showOutsideDays
            aria-label={t('automation.schedule.preview.calendar')}
            data-testid="schedule-preview-calendar"
            className={cn(
              'p-0',
              // Disable day button interactions (display only)
              '[&_button[data-day]]:pointer-events-none [&_button[data-day]]:cursor-default',
              // Override range styling to treat consecutive days as individual selections
              '[&_button[data-range-start=true]]:!rounded-md [&_button[data-range-middle=true]]:!rounded-md [&_button[data-range-middle=true]]:!bg-primary [&_button[data-range-middle=true]]:!text-primary-foreground [&_button[data-range-end=true]]:!rounded-md',
              // Single selected days styling
              '[&_button[data-selected-single=true]]:bg-primary [&_button[data-selected-single=true]]:text-primary-foreground [&_button[data-selected-single=true]]:rounded-md',
              // Outside month selected days - 50% opacity primary (all selection types)
              '[&_.rdp-outside_button[data-selected-single=true]]:bg-primary/50 [&_.rdp-outside_button[data-range-start=true]]:!bg-primary/50 [&_.rdp-outside_button[data-range-middle=true]]:!bg-primary/50 [&_.rdp-outside_button[data-range-end=true]]:!bg-primary/50',
              // Day cell sizing - button (yellow highlight) is 32px
              '[&_.rdp-day]:size-8 [&_.rdp-day_button]:!size-8 [&_.rdp-day_button]:rounded-md',
              // Row gap (gap-2 = 8px), column gap (gap-1 = 4px)
              '[&_.rdp-weeks]:gap-2 [&_.rdp-week]:gap-1',
            )}
          />
        </CardContent>

        <CardFooter
          className={cn(
            'pt-0',
            // Fixed-height card: the list scrolls as "View more" extends it.
            isHorizontal && 'h-full min-h-0 w-[260px] shrink-0 overflow-hidden',
          )}
        >
          <Flex
            direction="column"
            gap={2}
            width="full"
            className={cn(isHorizontal && 'h-full min-h-0')}
          >
            {/* List View Header */}
            <Flex align="center" width="full" paddingX={1}>
              <Text level="small">
                {t('automation.schedule.preview.listView')}
              </Text>
            </Flex>

            {/* Scheduled Runs List */}
            <Flex
              direction="column"
              gap={2}
              width="full"
              className={cn(isHorizontal && 'min-h-0 flex-1 overflow-y-auto')}
              data-testid="schedule-preview-runs-list"
            >
              {visibleRuns.map(run => (
                <ScheduleRunItem key={run.date.toISOString()} run={run} />
              ))}

              {/* TODO: Conditionally show this based on end condition and max invocations */}
              {showViewMore && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleViewMore}
                  data-testid="schedule-preview-view-more-button"
                >
                  {t('automation.schedule.preview.viewMore')}
                </Button>
              )}
            </Flex>
          </Flex>
        </CardFooter>
      </Flex>
    </Card>
  );
}

/**
 * Props for the ScheduleRunItem component.
 */
interface IScheduleRunItemProps {
  run: IScheduledRun;
}

/**
 * A single scheduled run item in the list view.
 * Displays the date, day of week, time range, and timezone
 * with a primary color indicator bar on the left.
 *
 * @param props - Component props
 * @returns JSX element for a single scheduled run item
 */
function ScheduleRunItem({ run }: IScheduleRunItemProps) {
  const formattedDate = formatDate(run.date, DATE_FORMAT_TOKENS.LONG_DATE);
  const startTimeDisplay = formatTimeDisplay(parseTime(run.startTime));
  const endTimeDisplay = run.endTime
    ? formatTimeDisplay(parseTime(run.endTime))
    : null;

  const timeDisplay = formatScheduleTimeDisplay(
    run.dayOfWeek,
    startTimeDisplay,
    endTimeDisplay,
    run.timezone,
  );

  return (
    <Flex align="start" gap={3} padding={2} width="full" className="rounded-md">
      {/* Primary color indicator bar */}
      <div className="w-1 h-9 rounded-sm bg-primary shrink-0" />

      <Flex direction="column">
        <Text level="small" weight="medium">
          {formattedDate}
        </Text>
        <Text level="small" color="muted" weight="normal" className="text-xs">
          {timeDisplay}
        </Text>
      </Flex>
    </Flex>
  );
}
